import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { format } from "date-fns";
import { ja } from "date-fns/locale";
import {
  CostItem,
  CostSection,
  TotalCostItem,
} from "@/features/realEstateDocumentFetch/components/styles";
import { getCompletePropertyRows } from "@/utils/propertyRows";
import { formatJPY } from "@/utils/formatJPY";
import {
  REGISTRY_ADJACENT_KIND_LABELS,
  REQUEST_TYPE_LABELS,
} from "../constants/priceMaster";
import {
  type EstimateResult,
  type ResearchAccess,
  type ResearchRequestFormData,
} from "../types";
import { type DeliverySchedule } from "../utils/deliverySchedule";
import { buildFullAddress } from "../utils/address";
import {
  buildAccessDisclaimer,
  formatAccessStation,
} from "../utils/accessText";
import { isResearchType } from "../utils/calculateEstimate";
import { getRequestedDocumentLabels } from "../utils/getRequestedDocumentLabels";
import { SectionCard } from "./formLayout";

interface RequestConfirmationStepProps {
  data: ResearchRequestFormData;
  files: File[];
  estimate: EstimateResult;
  /** 標準納期が要相談なら null */
  delivery: DeliverySchedule | null;
  /** 最寄駅の判定を問い合わせ中 */
  accessLoading: boolean;
}

interface SummaryRowProps {
  label: string;
  value: React.ReactNode;
}

const SummaryRow: React.FC<SummaryRowProps> = ({ label, value }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "180px 1fr" },
      gap: 1,
      py: 1,
    }}
  >
    <Typography
      variant="body2"
      sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.82rem" }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
    >
      {value}
    </Typography>
  </Box>
);

const formatRows = (rows: ResearchRequestFormData["chibanList"]): string => {
  const completedRows = getCompletePropertyRows(rows);
  return completedRows.length > 0
    ? completedRows
        .map(
          (row) => `${row.prefName}${row.location} ${row.chibanAndKaokuNumber}`
        )
        .join("\n")
    : "なし";
};

const formatRequestTypes = (data: ResearchRequestFormData): string =>
  data.requestTypes
    .map((requestType) => REQUEST_TYPE_LABELS[requestType])
    .join("、") || "-";

const formatRegistryScope = (data: ResearchRequestFormData): string =>
  data.registryAdjacent && data.registryAdjacentKind
    ? `隣接地も含む（隣接地は${
        REGISTRY_ADJACENT_KIND_LABELS[data.registryAdjacentKind]
      }）`
    : "対象地のみ";

const formatRate = (rate: number): string => `${Math.round(rate * 100)}%`;

const formatDate = (date: Date): string =>
  format(date, "yyyy年M月d日（E）", { locale: ja });

const formatStandardDelivery = (
  estimate: EstimateResult,
  delivery: DeliverySchedule | null
): string =>
  delivery
    ? `${estimate.standardDelivery}（${formatDate(delivery.standardDate)}）`
    : estimate.standardDelivery;

const formatDeliveryOption = (
  data: ResearchRequestFormData,
  delivery: DeliverySchedule | null
): string => {
  if (!data.specifyDeliveryDate) {
    return data.deadlineShortening === 0
      ? "短縮なし"
      : `${data.deadlineShortening}営業日短縮`;
  }
  const date = format(
    new Date(`${data.desiredDeliveryDate}T00:00:00`),
    "yyyy年M月d日（E）",
    { locale: ja }
  );
  if (delivery?.quoteRequired) return `${date}（個別見積）`;
  return delivery && delivery.shortening > 0
    ? `${date}（${delivery.shortening}営業日短縮）`
    : date;
};

const formatAccess = (access: ResearchAccess, loading: boolean): string => {
  if (loading) return "確認中";
  if (!access.judged) return "判定できませんでした（担当者が確認します）";
  return formatAccessStation(access);
};

const formatSpecialOptions = (data: ResearchRequestFormData): string => {
  const options = data.specialOptions;
  const details = [
    options.siteAreaSqm !== undefined
      ? `敷地面積: ${(options.siteAreaSqm ?? 0).toLocaleString()}㎡`
      : "",
    options.subdivisionUnits !== undefined
      ? `宅地戸建分譲戸数: ${(
          options.subdivisionUnits ?? 0
        ).toLocaleString()}戸`
      : "",
    options.condominiumUnits !== undefined
      ? `新築マンション戸数: ${(
          options.condominiumUnits ?? 0
        ).toLocaleString()}戸`
      : "",
    options.registeredLots !== undefined
      ? `登記筆数: ${(options.registeredLots ?? 0).toLocaleString()}筆`
      : "",
  ].filter((detail) => detail !== "");
  return details.join("\n") || "なし";
};

export const RequestConfirmationStep: React.FC<
  RequestConfirmationStepProps
> = ({ data, files, estimate, delivery, accessLoading }) => {
  const documents = getRequestedDocumentLabels(data);
  const fullAddress = buildFullAddress(data.addressPrefecture, data.address);
  const total =
    estimate.total === null ? "個別見積" : formatJPY(estimate.total);

  const basePriceLines: Array<[string, number | null]> =
    estimate.basePriceItems.map((item) => [
      `基本価格（${REQUEST_TYPE_LABELS[item.requestType]}）`,
      item.basePrice,
    ]);
  const costLines: Array<[string, number | null]> = [
    [
      `納期短縮加算（${formatRate(estimate.shorteningRate)}）`,
      estimate.shorteningAmount > 0 ? estimate.shorteningAmount : 0,
    ],
    [
      `特別加算（${formatRate(estimate.specialAdditionRate)}・最大率のみ）`,
      estimate.specialAdditionAmount > 0 ? estimate.specialAdditionAmount : 0,
    ],
    ["僻地加算", estimate.remoteAreaFee],
    ["車両必須加算", estimate.vehicleFee],
    ["郵送費", estimate.postalFee],
  ];

  return (
    <Stack spacing={2}>
      <SectionCard title="依頼内容">
        <SummaryRow label="対象住所" value={fullAddress} />
        <SummaryRow label="地番" value={formatRows(data.chibanList)} />
        <SummaryRow
          label="家屋番号"
          value={
            data.noKaokuNumber ? "該当なし" : formatRows(data.kaokuNumberList)
          }
        />
        <SummaryRow label="所有者名" value={data.ownerName || "不明"} />
        <SummaryRow label="依頼種別" value={formatRequestTypes(data)} />
        <SummaryRow
          label="現地調査（写真撮影）"
          value={data.onsitePhoto ? "あり" : "なし"}
        />
        <SummaryRow label="物件の規模" value={formatSpecialOptions(data)} />
        <SummaryRow
          label="最寄駅"
          value={formatAccess(estimate.access, accessLoading)}
        />
        <SummaryRow
          label="貴社から弊社に取得を依頼する資料"
          value={documents.join("、") || "なし"}
        />
        {data.selectedDocuments.includes("registry") ? (
          <SummaryRow
            label="登記簿謄本の範囲"
            value={formatRegistryScope(data)}
          />
        ) : null}
        <SummaryRow
          label="標準納期"
          value={formatStandardDelivery(estimate, delivery)}
        />
        <SummaryRow
          label={data.specifyDeliveryDate ? "希望納期" : "納期短縮"}
          value={formatDeliveryOption(data, delivery)}
        />
      </SectionCard>

      <SectionCard title="依頼者・関連情報">
        <SummaryRow label="担当者名" value={data.requesterName} />
        <SummaryRow label="メール" value={data.requesterEmail} />
        <SummaryRow label="電話番号" value={data.requesterPhone} />
        <SummaryRow
          label="郵送"
          value={
            data.needsPostalDelivery
              ? `希望あり（${data.postalAddress}）`
              : "なし"
          }
        />
        <SummaryRow
          label="関連資料"
          value={
            files.length > 0
              ? files.map((file) => file.name).join("、")
              : "なし"
          }
        />
        <SummaryRow label="補足・留意事項" value={data.notes || "なし"} />
      </SectionCard>

      <CostSection sx={{ mb: 0 }}>
        <Typography
          variant="subtitle2"
          component="h3"
          sx={{ fontSize: "14px", fontWeight: 700, color: "#065f46" }}
        >
          見積もり明細（税抜）
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#047857", fontSize: "0.82rem", mt: 0.5, mb: 1.5 }}
        >
          エリア{estimate.area}として算出しています。
        </Typography>
        {/* 基本価格のほかは、加算がある項目だけを出す */}
        {[
          ...basePriceLines,
          ...costLines.filter(([, amount]) => amount !== 0),
        ].map(([label, amount]) => (
          <CostItem key={label}>
            <Typography variant="body2">{label}</Typography>
            <Typography variant="body2" fontWeight={600}>
              {amount === null ? "個別見積" : formatJPY(amount)}
            </Typography>
          </CostItem>
        ))}
        <TotalCostItem>
          <span>税抜概算</span>
          <span>{total}</span>
        </TotalCostItem>
        {estimate.total === null ? (
          <Typography
            variant="body2"
            sx={{ mt: 1, color: "#047857", fontSize: "0.82rem" }}
          >
            {estimate.deliveryQuoteRequired
              ? "ご希望の納期は3営業日短縮より早いため、納期と金額は運営確認後にご案内します。"
              : "個別見積の項目は、運営確認後に金額をご案内します。"}
          </Typography>
        ) : null}
        {/* 判定できなかったときは「最寄駅」の欄に担当者が確認すると出すため、目安の説明は判定できたときだけ出す */}
        {data.requestTypes.some(isResearchType) && estimate.access.judged ? (
          <Typography
            variant="body2"
            sx={{ mt: 1, color: "#047857", fontSize: "0.82rem" }}
          >
            {buildAccessDisclaimer(estimate.access)}
          </Typography>
        ) : null}
        {documents.length > 0 ? (
          <Typography
            variant="body2"
            sx={{ mt: 1, color: "#047857", fontSize: "0.82rem" }}
          >
            取得を依頼する資料の実費（法務局などの手数料）は、調査費用に加えてご請求します。
          </Typography>
        ) : null}
      </CostSection>
    </Stack>
  );
};
